/* @flow */
import * as React from 'react';

import BoxBody from 'components/BoxBody';
import Button from 'components/Button';
import CheckboxList from 'components/CheckboxList';
import Dialog from 'components/Dialog';
import Group from 'entities/Group';
import GroupActions from 'groups/actions/GroupActions';
import GroupMemberships from 'entities/GroupMembership';
import Link from 'components/Link';
import LinkConstants from 'constants/LinkConstants';
import LoadingIndicator from 'components/LoadingIndicator';
import ModalTrigger from 'components/ModalTrigger';
import ScrollableArea from 'components/ScrollableArea';
import ScrollWatch from 'components/ScrollWatch';

import AddGroupOwnerModalConfirm from './AddGroupOwnerModalConfirm';

import './styles/AddGroupOwnerModal.scss';

const PER_PAGE = 10;

type Props = {
  group: Group,
  onRequestHide?: () => void,
  onFinish?: () => void,
};

type State = {
  allPotentialOwnersLoaded: boolean,
  checkedIds: Backbone$Id[],
  initLoadDone: boolean,
  page: number,
  potentialOwners: GroupMemberships[],
};

class AddGroupOwnerModal extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      allPotentialOwnersLoaded: false,
      checkedIds: [],
      initLoadDone: false,
      page: 0,
      potentialOwners: [],
    };
  }

  componentDidMount() {
    this.loadNextPage();
  }

  getPotentialOwnerOptions = () => {
    const results: Map<number, React$Element<*>> = new Map();

    this.state.potentialOwners.forEach(potentialOwner => {
      results.set(
        potentialOwner.getUserId(),
        <div className="AddGroupOwnerModal-potentialOwner">{potentialOwner.getName()}</div>
      );
    });

    return results;
  };

  loadNextPage = () => {
    const page = this.state.page + 1;
    const { group } = this.props;

    return GroupActions.loadPotentialOwners(group, page, PER_PAGE).then(potentialOwners => {
      // not already an owner.

      this.setState({
        page,
        initLoadDone: true,
        allPotentialOwnersLoaded: potentialOwners.length < PER_PAGE,
        potentialOwners: this.state.potentialOwners.concat(potentialOwners.models),
      });
    });
  };

  canSubmit = () => {
    return this.state.checkedIds.length > 0;
  };

  handlePotentialOwnerChange = (checkedIds: Backbone$Id[]) => {
    this.setState({ checkedIds });
  };

  renderAddOwnersButton = () => {
    return (
      <ModalTrigger modal={this.renderConfirmDialog()}>
        <Button
          disabled={!this.canSubmit()}
          className="AddGroupOwnerModal-addButton"
          eventElementName="AddGroupOwnerModal-addButton">
          Add
        </Button>
      </ModalTrigger>
    );
  };

  renderConfirmDialog = () => {
    return (
      <AddGroupOwnerModalConfirm
        group={this.props.group}
        checkedIds={this.state.checkedIds}
        onFinish={this.props.onFinish}
        onRequestHide={this.props.onRequestHide}
      />
    );
  };

  renderPotentialOwners = () => {
    return (
      <CheckboxList
        ref="potential-owners"
        className="AddGroupOwnerModal-list"
        options={this.getPotentialOwnerOptions()}
        onChange={this.handlePotentialOwnerChange}
      />
    );
  };

  renderTitle = (title: string) => {
    return (
      <div>
        <span>{title}</span>
      </div>
    );
  };

  renderLoadingIndicator = () => {
    if (this.state.allPotentialOwnersLoaded) {
      return null;
    }

    return (
      <ScrollWatch onEnter={this.loadNextPage}>
        <LoadingIndicator size="large" />
      </ScrollWatch>
    );
  };

  render() {
    let body = null;

    if (this.state.initLoadDone) {
      if (this.state.potentialOwners.length === 0) {
        body = (
          <div className="AddGroupOwnerModal-info">
            <p>
              To be promoted to class owner, participants must be 13 or older with a Remind account
              created online or in the app.
            </p>
            <p>Nobody is currently eligible to become a co-owner.</p>
            <Button href={LinkConstants.MOG} target="_blank" onClick={this.props.onRequestHide}>
              Learn more
            </Button>
          </div>
        );
      } else {
        body = (
          <div>
            <div className="AddGroupOwnerModal-info">
              Not seeing the person you’d like to add?{' '}
              <Link href={LinkConstants.MOG} target="_blank" onClick={this.props.onRequestHide}>
                Add them
              </Link>
            </div>
            <ScrollableArea className="AddGroupOwnerModal-scrollArea">
              {this.renderPotentialOwners()}
              {this.renderLoadingIndicator()}
            </ScrollableArea>
            {this.renderAddOwnersButton()}
          </div>
        );
      }
    } else {
      body = <LoadingIndicator />;
    }

    return (
      <Dialog
        eventScreenName="AddGroupOwnerModal"
        {...this.props}
        className="AddGroupOwnerModal"
        title="Add class owners"
        size="small">
        <BoxBody className="AddGroupOwnerModal-body" divided full>
          {body}
        </BoxBody>
      </Dialog>
    );
  }
}
export default AddGroupOwnerModal;
